
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Divider,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Stack,
  Popover,
  IconButton,
  Tooltip,
} from '@mui/material';

import {
  Security as SecurityIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon,
  RestartAlt as RestartAltIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function PermissionPage() {
  const token = localStorage.getItem('accessToken');

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    [token]
  );

  // Client's own permissions
  const [clientOwnPermissions, setClientOwnPermissions] = useState(null);
  const [loadingOwnPermissions, setLoadingOwnPermissions] = useState(false);

  // Roles, modules and permissions
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedModule, setSelectedModule] = useState('All Modules');

  const [permissions, setPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);

  const [hasChanges, setHasChanges] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);

  // Filter popover
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [draftRole, setDraftRole] = useState('');
  const [draftModule, setDraftModule] = useState('All Modules');

  const filtersOpen = Boolean(filterAnchorEl);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch client's own permissions
  useEffect(() => {
    let cancelled = false;

    const fetchClientOwnPermissions = async () => {
      setLoadingOwnPermissions(true);

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/elearning_settings/client-own-permission/`,
          { headers }
        );

        if (!cancelled) {
          setClientOwnPermissions(response.data);
        }
      } catch (err) {
        if (cancelled) return;

        console.error('Failed to fetch client permissions:', err);

        setSnackbar({
          open: true,
          message: 'Failed to load client permissions',
          severity: 'error',
        });
      } finally {
        if (!cancelled) {
          setLoadingOwnPermissions(false);
        }
      }
    };

    fetchClientOwnPermissions();

    return () => {
      cancelled = true;
    };
  }, [headers]);

  // Fetch roles and modules
  useEffect(() => {
    let cancelled = false;

    const fetchRolesAndModules = async () => {
      try {
        const [rolesRes, modulesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/staff/user-roles/`, { headers }),
          axios.get(
            `${API_BASE_URL}/api/elearning_settings/client/modules/`,
            { headers }
          ),
        ]);

        if (cancelled) return;

        setRoles(rolesRes.data);
        setModules(modulesRes.data);
      } catch (err) {
        if (cancelled) return;

        console.error('Failed to fetch roles/modules:', err);

        setSnackbar({
          open: true,
          message: 'Failed to load roles and modules',
          severity: 'error',
        });
      }
    };

    fetchRolesAndModules();

    return () => {
      cancelled = true;
    };
  }, [headers]);

  // Fetch permissions for the selected role and module
  useEffect(() => {
    if (!selectedRole || modules.length === 0) {
      setPermissions([]);
      setOriginalPermissions([]);
      setHasChanges(false);
      setLoadingPermissions(false);
      return undefined;
    }

    let cancelled = false;

    const fetchPermissions = async () => {
      setLoadingPermissions(true);

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/elearning_settings/module-form-permissions/?role_id=${selectedRole}`,
          { headers }
        );

        if (cancelled) return;

        const roleData = response.data.find(
          (item) => item.role_id === Number(selectedRole)
        );

        const permissionsData = roleData?.permissions || [];
        const formattedPermissions = [];

        modules.forEach((module) => {
          if (
            selectedModule === 'All Modules' ||
            module.module_name === selectedModule
          ) {
            module.forms.forEach((form) => {
              const existingPerm = permissionsData.find(
                (permission) => permission.form_id === form.form_id
              );

              formattedPermissions.push({
                id: form.form_id,
                module: module.module_name,
                module_id: module.module_id,
                form: form.form_name,
                read: existingPerm?.can_read || false,
                create: existingPerm?.can_create || false,
                update: existingPerm?.can_update || false,
                delete: existingPerm?.can_delete || false,
              });
            });
          }
        });

        setPermissions(formattedPermissions);
        setOriginalPermissions(
          JSON.parse(JSON.stringify(formattedPermissions))
        );
        setHasChanges(false);
      } catch (err) {
        if (cancelled) return;

        console.error('Failed to fetch permissions:', err);

        setSnackbar({
          open: true,
          message: 'Failed to load permissions',
          severity: 'error',
        });
      } finally {
        if (!cancelled) {
          setLoadingPermissions(false);
        }
      }
    };

    fetchPermissions();

    return () => {
      cancelled = true;
    };
  }, [selectedRole, selectedModule, modules, headers]);

  // Unique module names
  const moduleNames = useMemo(
    () => [
      'All Modules',
      ...new Set(modules.map((module) => module.module_name)),
    ],
    [modules]
  );

  // Displayed permissions
  const displayedPermissions = useMemo(() => {
    if (selectedModule === 'All Modules') return permissions;

    return permissions.filter(
      (permission) => permission.module === selectedModule
    );
  }, [permissions, selectedModule]);

  // Filter handlers
  const handleOpenFilters = (event) => {
    setDraftRole(selectedRole);
    setDraftModule(selectedModule);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilters = () => {
    setSelectedRole(draftRole);
    setSelectedModule(draftModule);
    setFilterAnchorEl(null);
  };

  const handleResetFilters = () => {
    setDraftRole('');
    setDraftModule('All Modules');

    setSelectedRole('');
    setSelectedModule('All Modules');

    setPermissions([]);
    setOriginalPermissions([]);
    setHasChanges(false);

    setFilterAnchorEl(null);
  };

  // Toggle a permission column for currently displayed rows
  const toggleColumn = (field) => {
    if (displayedPermissions.length === 0) return;

    const visibleIds = new Set(
      displayedPermissions.map((row) => row.id)
    );

    const allChecked = displayedPermissions.every(
      (row) => row[field]
    );

    setPermissions((previous) =>
      previous.map((row) =>
        visibleIds.has(row.id)
          ? { ...row, [field]: !allChecked }
          : row
      )
    );

    setHasChanges(true);
  };

  // Toggle an individual permission
  const togglePermission = (rowId, field) => {
    setPermissions((previous) =>
      previous.map((row) =>
        row.id === rowId
          ? { ...row, [field]: !row[field] }
          : row
      )
    );

    setHasChanges(true);
  };

  // Save permissions
  const handleSavePermissions = async () => {
    if (!selectedRole) {
      setSnackbar({
        open: true,
        message: 'Please select a role first',
        severity: 'warning',
      });

      return;
    }

    setSavingPermissions(true);

    try {
      const permissionsPayload = permissions.map((permission) => {
        const module = modules.find(
          (item) => item.module_name === permission.module
        );

        return {
          module_id: module?.module_id || null,
          form_id: permission.id,
          can_create: permission.create,
          can_read: permission.read,
          can_update: permission.update,
          can_delete: permission.delete,
        };
      });

      const payload = {
        role_id: selectedRole,
        permissions: permissionsPayload,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/elearning_settings/module-form-permissions/`,
        payload,
        { headers }
      );

      if (response.status === 200 || response.status === 201) {
        setOriginalPermissions(
          JSON.parse(JSON.stringify(permissions))
        );

        setHasChanges(false);

        setSnackbar({
          open: true,
          message: 'Permissions saved successfully!',
          severity: 'success',
        });
      }
    } catch (err) {
      console.error('Failed to save permissions:', err);

      setSnackbar({
        open: true,
        message:
          err.response?.data?.message || 'Failed to save permissions',
        severity: 'error',
      });
    } finally {
      setSavingPermissions(false);
    }
  };

  // Cancel changes
  const handleCancelPermissions = () => {
    setPermissions(
      JSON.parse(JSON.stringify(originalPermissions))
    );

    setHasChanges(false);

    setSnackbar({
      open: true,
      message: 'Changes have been discarded',
      severity: 'info',
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((previous) => ({
      ...previous,
      open: false,
    }));
  };

  // Theme-aware card
  const cardSx = {
    width: '100%',
    border: 1,
    borderColor: 'divider',
    borderRadius: 3,
    bgcolor: 'background.paper',
    color: 'text.primary',
    boxShadow: (theme) =>
      theme.palette.mode === 'dark'
        ? '0 4px 18px rgba(0, 0, 0, 0.22)'
        : '0 4px 18px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
  };

  // Dynamic table header colors
  const headerCellSx = {
    fontWeight: 700,
    color: 'primary.contrastText',
    fontSize: '0.75rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    px: 2,
    py: 1.5,
    borderBottom: 1,
    borderColor: 'divider',

    bgcolor: 'primary.main',

    '&.MuiTableCell-stickyHeader': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
    },
  };

  // Dynamic table body colors
  const bodyCellSx = {
    color: 'text.primary',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    px: 2,
    py: 1.25,
  };

  // More visible checked, unchecked and disabled checkboxes
  const checkboxSx = {
    color: (theme) =>
      theme.palette.mode === 'dark'
        ? theme.palette.grey[400]
        : theme.palette.grey[600],

    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },

    '&.Mui-checked': {
      color: 'primary.main',
    },

    '&.MuiCheckbox-indeterminate': {
      color: 'primary.main',
    },

    '&.Mui-disabled': {
      color: (theme) =>
        theme.palette.mode === 'dark'
          ? theme.palette.grey[700]
          : theme.palette.grey[400],
      opacity: 1,
    },
  };

  const headerCheckboxSx = {
    ...checkboxSx,
    color: 'primary.contrastText',
    '&.Mui-checked, &.MuiCheckbox-indeterminate': {
      color: 'primary.contrastText',
    },
  };

  // Theme-aware dropdown
  const selectSx = {
    borderRadius: 2,
    bgcolor: 'background.paper',
    color: 'text.primary',

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'divider',
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'text.disabled',
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
    },

    '& .MuiSvgIcon-root': {
      color: 'text.disabled',
    },
  };

  // Theme-aware dropdown menu
  const menuProps = {
    PaperProps: {
      sx: {
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: 1,
        borderColor: 'divider',
        boxShadow: 4,

        '& .MuiMenuItem-root': {
          color: 'text.primary',
        },

        '& .MuiMenuItem-root:hover': {
          bgcolor: 'action.hover',
        },

        '& .MuiMenuItem-root.Mui-selected': {
          bgcolor: 'action.selected',
        },

        '& .MuiMenuItem-root.Mui-selected:hover': {
          bgcolor: 'action.hover',
        },
      },
    },
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        p: { xs: 1.5, sm: 2.5, md: 3 },
        bgcolor: 'background.default',
        color: 'text.primary',
        boxSizing: 'border-box',
      }}
    >
      <Grid container spacing={2}>
        {/* My Permissions Card - preserved for future use */}
        {/*
        <Grid item xs={12}>
          <Card sx={cardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SecurityIcon
                  sx={{ fontSize: 32, mr: 1, color: 'primary.main' }}
                />
                <Typography variant="h5" fontWeight={600}>
                  My Permissions
                </Typography>
              </Box>

              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ mb: 3 }}
              >
                Your current access permissions
              </Typography>

              {loadingOwnPermissions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : clientOwnPermissions ? (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 1,
                    }}
                  >
                    <PersonIcon sx={{ color: 'text.primary', mr: 2 }} />
                    <Typography
                      sx={{
                        color: 'text.primary',
                        fontWeight: 500,
                        minWidth: '120px',
                      }}
                    >
                      Client Code :
                    </Typography>
                    <Typography color="text.primary">
                      {clientOwnPermissions.client_code || 'N/A'}
                    </Typography>
                  </Box>

                  <Divider sx={{ borderColor: 'divider', my: 1 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                      p: 1,
                    }}
                  >
                    <SecurityIcon
                      sx={{ color: 'text.primary', mr: 2, mt: 0.5 }}
                    />
                    <Typography
                      sx={{
                        color: 'text.primary',
                        fontWeight: 500,
                        minWidth: '120px',
                      }}
                    >
                      Permissions :
                    </Typography>

                    <Box>
                      <Typography variant="body2" color="text.primary">
                        Read: {clientOwnPermissions.can_read ? '✓ Yes' : '✗ No'}
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        Create: {clientOwnPermissions.can_create ? '✓ Yes' : '✗ No'}
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        Update: {clientOwnPermissions.can_update ? '✓ Yes' : '✗ No'}
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        Delete: {clientOwnPermissions.can_delete ? '✓ Yes' : '✗ No'}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: 'divider', my: 1 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 1,
                    }}
                  >
                    <SettingsIcon sx={{ color: 'text.primary', mr: 2 }} />
                    <Typography
                      sx={{
                        color: 'text.primary',
                        fontWeight: 500,
                        minWidth: '120px',
                      }}
                    >
                      Status :
                    </Typography>
                    <Typography color="text.primary">
                      {clientOwnPermissions.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Alert severity="info">
                  No permission data available
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        */}

        {/* Set Client Permissions */}
        <Grid item xs={12} sx={{ width: '100%', minWidth: 0 }}>
          <Card sx={cardSx}>
            <CardContent
              sx={{
                p: { xs: 2, sm: 3 },
                '&:last-child': { pb: 3 },
              }}
            >
              {/* Header */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 3 }}
              >
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ mb: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                         borderRadius: '0 !important',
                        bgcolor: 'action.hover',
                        color: 'primary.main',
                      }}
                    >
                      <SettingsIcon sx={{ fontSize: 26 }} />
                    </Box>

                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.2rem', sm: '1.5rem' },
                        color: 'text.primary',
                      }}
                    >
                      Set Client Permissions
                    </Typography>
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{ color: 'text.disabled' }}
                  >
                    Configure module and form permissions for different roles.
                  </Typography>
                </Box>

                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  {selectedRole && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <SecurityIcon
                        sx={{ fontSize: 18, color: 'primary.main' }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 500,
                        }}
                      >
                        {displayedPermissions.length} forms
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    startIcon={<FilterListIcon />}
                    onClick={handleOpenFilters}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      color: 'primary.contrastText',
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }}
                  >
                    Filter
                  </Button>

                  {/* Filter Popover */}
                  <Popover
                    open={filtersOpen}
                    anchorEl={filterAnchorEl}
                    onClose={handleCloseFilters}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      sx: {
                        width: { xs: 290, sm: 340 },
                        maxWidth: 'calc(100vw - 24px)',
                        mt: 1,
                        p: 2.5,
                        borderRadius: 3,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        backgroundImage: 'none',
                        boxShadow: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '0 8px 28px rgba(0,0,0,0.4)'
                            : '0 8px 28px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    <Stack spacing={2.5}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="h6" fontWeight={700}>
                          Filters
                        </Typography>

                        <Tooltip title="Close filters">
                          <IconButton
                            size="small"
                            onClick={handleCloseFilters}
                            aria-label="Close filters"
                            sx={{
                              color: 'primary.contrastText',
                              bgcolor: 'primary.main',
                              '&:hover': { bgcolor: 'primary.dark' },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      <Divider sx={{ borderColor: 'divider' }} />

                      <FormControl fullWidth size="small">
                        <InputLabel id="permission-role-filter-label">
                          Select Role
                        </InputLabel>

                        <Select
                          labelId="permission-role-filter-label"
                          value={draftRole}
                          label="Select Role"
                          onChange={(event) =>
                            setDraftRole(event.target.value)
                          }
                          sx={selectSx}
                          MenuProps={menuProps}
                        >
                          {roles.map((role) => (
                            <MenuItem
                              key={role.id || role.role_id}
                              value={role.id || role.role_id}
                            >
                              {role.name || role.role_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <InputLabel id="permission-module-filter-label">
                          Select Module
                        </InputLabel>

                        <Select
                          labelId="permission-module-filter-label"
                          value={draftModule}
                          label="Select Module"
                          onChange={(event) =>
                            setDraftModule(event.target.value)
                          }
                          sx={selectSx}
                          MenuProps={menuProps}
                        >
                          {moduleNames.map((moduleName) => (
                            <MenuItem key={moduleName} value={moduleName}>
                              {moduleName.charAt(0).toUpperCase() +
                                moduleName.slice(1)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Stack direction="row" spacing={1.5}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<RestartAltIcon />}
                          onClick={handleResetFilters}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            borderColor: 'divider',
                            color: 'primary.contrastText',
                            bgcolor: 'primary.main',
                            '&:hover': { bgcolor: 'primary.dark' },
                          }}
                        >
                          Reset
                        </Button>

                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleApplyFilters}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                          }}
                        >
                          Apply
                        </Button>
                      </Stack>
                    </Stack>
                  </Popover>
                </Stack>
              </Stack>

              <Divider sx={{ mb: 3, borderColor: 'divider' }} />

              {/* Applied filter summary */}
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{ mb: 2 }}
              >
                {selectedRole && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.5,
                      bgcolor: 'action.hover',
                      border: 1,
                      borderColor: 'primary.main',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.primary"
                      fontWeight={700}
                    >
                      Role
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.primary"
                      fontWeight={600}
                    >
                      {roles.find(
                        (role) =>
                          String(role.id || role.role_id) ===
                          String(selectedRole)
                      )?.name ||
                        roles.find(
                          (role) =>
                            String(role.id || role.role_id) ===
                            String(selectedRole)
                        )?.role_name ||
                        'Selected role'}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    border: 1,
                    borderColor: 'primary.main',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.primary"
                    fontWeight={700}
                  >
                    Module
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.primary"
                    fontWeight={600}
                  >
                    {selectedModule}
                  </Typography>
                </Box>
              </Stack>

              {/* Permissions table */}
              {selectedRole ? (
                loadingPermissions ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 8,
                      gap: 2,
                    }}
                  >
                    <CircularProgress />
                    <Typography variant="body2" color="text.disabled">
                      Loading permissions...
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer
                    component={Paper}
                    variant="contained"
                    sx={{
                      width: '100%',
                      maxHeight: 480,
                      overflow: 'auto',
                      borderRadius: 2,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      backgroundImage: 'none',

                      '& .MuiTable-root': {
                        bgcolor: 'background.paper',
                      },
                    }}
                  >
                    <Table
                      size="small"
                      stickyHeader
                      sx={{ minWidth: 760 }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>
                            S.No
                          </TableCell>

                          <TableCell sx={headerCellSx}>
                            Module Name
                          </TableCell>

                          <TableCell sx={headerCellSx}>
                            Form Name
                          </TableCell>

                          {[
                            { label: 'Read', field: 'read' },
                            { label: 'Create', field: 'create' },
                            { label: 'Update', field: 'update' },
                            { label: 'Delete', field: 'delete' },
                          ].map(({ label, field }) => (
                            <TableCell
                              key={field}
                              align="center"
                              sx={headerCellSx}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="center"
                                spacing={0.5}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: 'inherit',
                                  }}
                                >
                                  {label}
                                </Typography>

                                <Checkbox
                                  sx={headerCheckboxSx}
                                  size="small"
                                  disabled={
                                    displayedPermissions.length === 0
                                  }
                                  indeterminate={
                                    displayedPermissions.some(
                                      (row) => row[field]
                                    ) &&
                                    !displayedPermissions.every(
                                      (row) => row[field]
                                    )
                                  }
                                  checked={
                                    displayedPermissions.length > 0 &&
                                    displayedPermissions.every(
                                      (row) => row[field]
                                    )
                                  }
                                  onChange={() => toggleColumn(field)}
                                  inputProps={{
                                    'aria-label': `Toggle all ${label} permissions`,
                                  }}
                                />
                              </Stack>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {displayedPermissions.length > 0 ? (
                          displayedPermissions.map((row, index) => (
                            <TableRow
                              key={row.id}
                              hover
                              sx={{
                                bgcolor: 'background.paper',

                                '&:last-child td, &:last-child th': {
                                  borderBottom: 0,
                                },

                                '&:hover': {
                                  bgcolor: 'action.hover',
                                },

                                '& .MuiTableCell-root': {
                                  transition: 'background-color 120ms ease',
                                },
                              }}
                            >
                              <TableCell sx={bodyCellSx}>
                                {index + 1}
                              </TableCell>

                              <TableCell
                                sx={{
                                  ...bodyCellSx,
                                  minWidth: 150,
                                  fontWeight: 500,
                                }}
                              >
                                {row.module.charAt(0).toUpperCase() +
                                  row.module.slice(1)}
                              </TableCell>

                              <TableCell
                                sx={{
                                  ...bodyCellSx,
                                  minWidth: 180,
                                }}
                              >
                                {row.form.charAt(0).toUpperCase() +
                                  row.form.slice(1)}
                              </TableCell>

                              {['read', 'create', 'update', 'delete'].map(
                                (field) => (
                                  <TableCell
                                    key={field}
                                    align="center"
                                    sx={bodyCellSx}
                                  >
                                    <Checkbox
                                      sx={checkboxSx}
                                      size="small"
                                      checked={Boolean(row[field])}
                                      onChange={() =>
                                        togglePermission(row.id, field)
                                      }
                                      inputProps={{
                                        'aria-label': `${field} permission for ${row.form}`,
                                      }}
                                    />
                                  </TableCell>
                                )
                              )}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              align="center"
                              sx={{
                                py: 7,
                                borderBottom: 0,
                                color: 'text.primary',
                              }}
                            >
                              <Stack alignItems="center" spacing={1}>
                                <SecurityIcon
                                  sx={{
                                    fontSize: 36,
                                    color: 'text.disabled',
                                  }}
                                />

                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                  }}
                                >
                                  No forms available
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ color: 'text.disabled' }}
                                >
                                  No forms found for the selected module.
                                </Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    px: 2,
                    border: 1,
                    borderStyle: 'dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    textAlign: 'center',
                  }}
                >
                  <SecurityIcon
                    sx={{
                      fontSize: 42,
                      color: 'text.disabled',
                      mb: 1.5,
                    }}
                  />

                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      mb: 0.5,
                    }}
                  >
                    Select a role to get started
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: 'text.disabled' }}
                  >
                    Choose a role from the Filter menu to view and configure
                    its permissions.
                  </Typography>
                </Box>
              )}
            </CardContent>

            {/* Save and Cancel */}
            {selectedRole && hasChanges && (
              <CardActions
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  p: { xs: 2, sm: 2.5 },
                  borderTop: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleCancelPermissions}
                  disabled={savingPermissions}
                  startIcon={<CancelIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    borderColor: 'divider',
                    color: 'primary.contrastText',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                 data-app-cancel="true">
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSavePermissions}
                  disabled={savingPermissions}
                  startIcon={
                    savingPermissions ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    boxShadow: 'none',

                    '&:hover': {
                      boxShadow: 'none',
                    },
                  }}
                >
                  {savingPermissions ? 'Saving...' : 'Save Permissions'}
                </Button>
              </CardActions>
            )}
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PermissionPage;
